import en from '@/src/messages/en.json';
import type { ApartmentReviews } from '@/src/lib/api/endpoints/apartmentReviews';
export type ReputationCategory = {
  id?: string;
  category: string;
  summary?: string;
  reviewCount?: number;
  scorePercent?: number;
};

export type ReputationTheme = { label: string; title: string; summary: string };

export type ReputationPresentation = {
  averageRating?: number;
  totalReviews?: number;
  themes?: Array<ReputationTheme>;
  categories?: Array<ReputationCategory>;
};

export const validReviewCount = (value: number | undefined) =>
  value !== undefined && Number.isInteger(value) && value >= 0 ? value : undefined;

export const validScorePercent = (value: number | undefined) =>
  value !== undefined && Number.isFinite(value) && value >= 0 && value <= 100 ? value : undefined;

export function selectCategoryHighlights(categories: Array<ReputationCategory>): Array<ReputationCategory> {
  const unique = categories.filter(
    (item, index) => categories.findIndex((other) => other.category === item.category) === index,
  );
  if (unique.length < 5) return unique;
  const selected = new Set<ReputationCategory>();
  const scores = unique.filter((category) => validScorePercent(category.scorePercent) !== undefined);
  const counts = unique.filter((category) => validReviewCount(category.reviewCount) !== undefined);
  const add = (items: Array<ReputationCategory>) => {
    const next = items.find((item) => !selected.has(item));
    if (next) selected.add(next);
  };
  add([...scores].sort((a, b) => b.scorePercent! - a.scorePercent!));
  add([...counts].sort((a, b) => b.reviewCount! - a.reviewCount!));
  add([...scores].sort((a, b) => a.scorePercent! - b.scorePercent!));
  const remaining = [...unique].sort(
    (a, b) => (validReviewCount(b.reviewCount) ?? -1) - (validReviewCount(a.reviewCount) ?? -1),
  );
  while (selected.size < 5) add(remaining);
  return [...selected];
}

const categoryLabels = en.hdp.reviewCategoryLabels;
type ReviewCategoryId = keyof typeof categoryLabels;

export function isReviewCategoryId(id: string | undefined): id is ReviewCategoryId {
  return id !== undefined && Object.hasOwn(categoryLabels, id);
}

export function toReputationPresentation(data: NonNullable<ApartmentReviews['reputation']>): ReputationPresentation {
  const presentation: ReputationPresentation = {
    averageRating: data.averageRating,
    totalReviews: data.totalReviews,
    categories: data.categories?.flatMap((category) => {
      if (!category.category?.trim()) return [];
      return [
        {
          id: category.category,
          category: isReviewCategoryId(category.category)
            ? categoryLabels[category.category]
            : category.category.replaceAll('_', ' '),
          summary: category.summary,
          reviewCount: category.reviewCount,
          // Reputation supplies 0–100 scores; ingestion forwards them without rescaling.
          scorePercent: category.score,
        },
      ];
    }),
  };
  return presentation;
}
